/**
 * Custody voucher of AUTOSUR ("Vale de resguardo de artículos"), WITHOUT price.
 * Pure: it only builds the pdfmake document definition (logos, user and dates
 * come in `ctx`), so it is tested without a browser.
 *
 * Format sent by AUTOSUR on 2026-11-03 (Vale_de_Resguardo_de_Articulos.docx),
 * replacing the one of the annotated capture: header with logo, title and a
 * folio to fill by hand, four sections with blue bars (collaborator, articles,
 * declaration, signatures) and a footer with the control legend and the SAAF
 * logo. One document per employee: the individual voucher of the asset detail
 * has one article, the general voucher of Reportes > Vales de resguardo all of
 * them. The articles table takes the style of the generic voucher (alternate
 * gray rows, bold header, no grid; EmployeesAssetsPDF, last branch) instead of
 * the Word's blue grid, and has no blank filler rows (user's decision).
 *
 * With ctx.signature the RECIBE box carries the drawn signature and the signing
 * date, and the electronic signature legend goes under the signatures; without
 * it the document is the paper version, with blank lines to fill by hand.
 */

// Company of Grupo AutoSur (all its subcompanies use this voucher). Which
// clients get which voucher lives in lib/vouchers/clientConfig.
export const AUTOSUR_COMPANY_ID = '69ea3f02557b44bce01af700';

// "SD" (sin dato) is how AUTOSUR fills empty brand/model/serie (hundreds of
// assets): treated as empty.
const EMPTY = /^\s*(sd|s\/d|n\/a|na|-+)?\s*$/i;
export const printable = (v) => (v == null || EMPTY.test(String(v)) ? '' : String(v).trim());

// Every field of the voucher is printed; an empty one says "N/A".
export const NO_DATA = 'N/A';
const orNoData = (v) => printable(v) || NO_DATA;

export const custodyNoteLines = (notes) =>
  printable(notes)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const fullName = (person) =>
  person ? [person.name, person.lastname].map(printable).filter(Boolean).join(' ') : '';

/**
 * vale_resguardo_{number}_{YYYY-MM-DD_HHmm}.pdf, with the number made file-safe.
 * The back accepts only this shape (CUSTODY_VOUCHER_NAME in src/lib/custody_vouchers.js).
 */
export const custodyVoucherFileName = (asset, stamp) =>
  `vale_resguardo_${(printable(asset && asset.number) || 'activo')
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .slice(0, 120)}_${stamp}.pdf`;

/** Name printed as "Generado por" under the signature: full name, or the e-mail. */
export const signerName = (user) =>
  (user &&
    ([user.name, user.lastname].map(printable).filter(Boolean).join(' ') ||
      printable(user.email))) ||
  '';

/** A signed voucher in asset._files: the back never lets it be removed or edited. */
export const isLockedFile = (file) => !!(file && file.locked);

/**
 * Strokes of signature_pad (toData()) scaled to a resized canvas, so turning
 * the phone keeps the signature instead of clearing it. Pure: returns a copy.
 */
export const scaleStrokes = (strokes, sx, sy) =>
  (strokes || []).map((stroke) => ({
    ...stroke,
    points: (stroke.points || []).map((p) => ({ ...p, x: p.x * sx, y: p.y * sy })),
  }));

/**
 * The location of an asset ("id.id.id": location and sublocations, see
 * getLocationPath in lib/assets/assets_helpers) as names, "Oficina > Piso 1".
 * Ids missing from the catalog are skipped. The back resolves the same for the
 * employees report (locationName in src/lib/custody_vouchers.js).
 * @param {string} path - asset.location
 * @param {Object[]} catalog - [{id, name}], locations and sublocations together
 */
export const locationName = (path, catalog) =>
  String(path || '')
    .split('.')
    .filter(Boolean)
    .map((id) => ((catalog || []).find((l) => String(l.id || l._id) === id) || {}).name)
    .filter(Boolean)
    .join(' > ');

// The folio is filled by hand for now (AUTOSUR, 2026-11-03: no consecutive).
export const FOLIO = 'VR-____-____';
export const BLANK_DATE = '____ / ____ / ______';
const BLANK_LINE = '________________________________';

// The employee model (SAAF_Back src/app/model/employee.js) has no area or
// department field, only per-client groups and custom fields: always "N/A".
const AREA = NO_DATA;

export const DECLARATION =
  'Por medio del presente, hago constar que recibo los artículos descritos en este vale, en ' +
  'las condiciones señaladas, y que quedan bajo mi resguardo para el desempeño de mis ' +
  'funciones. Me comprometo a utilizarlos exclusivamente para fines laborales, conservarlos ' +
  'adecuadamente y reportar de inmediato cualquier daño, pérdida, robo o falla. Asimismo, me ' +
  'comprometo a devolverlos cuando sean requeridos por la empresa, exista cambio de puesto o ' +
  'ubicación, o termine la relación laboral, considerando el desgaste normal por uso.';

export const FOOTER_TEXT = 'Documento de control interno | Vale de resguardo de artículos';

export const ITEM_HEADERS = [
  'Cant.',
  'Artículo / descripción',
  'Marca / modelo',
  'No. de serie',
  'No. de activo',
  'Estado',
  'Ubicación / observaciones',
];

/** "DD/MM/YYYY HH:mm" of the signature -> "DD/MM/YYYY". */
const signedDate = (signature) => String(signature.signedAt).slice(0, 10);

/**
 * One article as the seven cells of the table, each a list of text lines
 * (stacked in the cell). Pure; shared by the PDF and the HTML preview.
 * @param {Object} asset - a record asset: name, description, brand, model,
 *   serie, number, physicalState, locationName, custodyNotes
 */
export const itemCells = (asset) => {
  const name = printable(asset.name);
  const description = printable(asset.description);
  const brandModel = [asset.brand, asset.model].map(printable).filter(Boolean).join(' / ');
  const where = [printable(asset.locationName)]
    .concat(custodyNoteLines(asset.custodyNotes))
    .filter(Boolean);
  return [
    ['1'],
    // AUTOSUR often repeats the name in the description: printed only if it adds something.
    [name || NO_DATA].concat(description && description !== name ? [description] : []),
    [brandModel || NO_DATA],
    [orNoData(asset.serie)],
    [orNoData(asset.number)],
    [orNoData(asset.physicalState)],
    where.length ? where : [NO_DATA],
  ];
};

/**
 * Section 1 as [label, value] pairs in the Word's order: two columns, row by
 * row; null is an empty cell.
 */
export const collaboratorData = (record, ctx) => [
  ['Nombre del colaborador:', orNoData(fullName(record))],
  ['No. de empleado:', orNoData(record.key)],
  ['Puesto:', orNoData(record.position)],
  ['Área / Departamento:', AREA],
  ['Unidad de negocio:', orNoData(ctx.subcompany)],
  null,
  ['Fecha de entrega:', ctx.signature ? signedDate(ctx.signature) : BLANK_DATE],
  null,
];

/**
 * Who hands the articles over: the session user, and the position if the user
 * has one (the user model of today has none, so only the name).
 */
export const deliveredBy = (user) =>
  [signerName(user), printable(user && user.position)].filter(Boolean).join(' - ');

const signatureNote = (signature) =>
  `Firmado electrónicamente el ${signature.signedAt}. Generado por: ${signature.generatedBy}.`;

/**
 * What the voucher says, for the HTML preview of the signing modals (a PDF in
 * an <iframe> does not show on Android Chrome). Same texts as the paper PDF.
 */
export const custodyVoucherPreview = (record, ctx) => ({
  title: 'VALE DE RESGUARDO DE ARTÍCULOS',
  folio: FOLIO,
  collaborator: collaboratorData(record, { ...ctx, signature: null }).filter(Boolean),
  headers: ITEM_HEADERS,
  items: (record.assets || []).map(itemCells),
  declaration: DECLARATION,
  delivered: deliveredBy(ctx.user) || 'Nombre y puesto',
  received: orNoData(fullName(record)),
  blankDate: `Fecha: ${BLANK_DATE}`,
  footer: FOOTER_TEXT,
});

// Colors of the Word.
const BLUE = '#1F4E79';
const LIGHT_BLUE = '#D9EAF7';
const HEADER_GRAY = '#F2F2F2';
const TEXT = '#333333';
// The generic voucher's table (EmployeesAssetsPDF, last branch).
const ROW_TEXT = '#45535b';
const ROW_GRAY = '#f4f4f4';
const ROW_LINE = '#e9ecef';

const header = ({ logoAutosur }) => ({
  table: {
    widths: ['*', '*', '*'],
    body: [
      [
        { image: logoAutosur, width: 86, alignment: 'center', margin: [0, 8, 0, 6] },
        {
          text: 'VALE DE RESGUARDO DE ARTÍCULOS',
          bold: true,
          fontSize: 14,
          color: BLUE,
          alignment: 'center',
          margin: [0, 6, 0, 6],
        },
        {
          text: `Folio:\n${FOLIO}`,
          bold: true,
          fontSize: 8.5,
          color: TEXT,
          alignment: 'center',
          margin: [0, 12, 0, 6],
        },
      ],
    ],
  },
  layout: { defaultBorder: false, fillColor: () => HEADER_GRAY },
  margin: [0, 0, 0, 4],
});

const sectionBar = (text) => ({
  table: {
    widths: ['*'],
    body: [[{ text, bold: true, color: '#FFFFFF', margin: [2, 3, 0, 3] }]],
  },
  layout: { defaultBorder: false, fillColor: () => BLUE },
  margin: [0, 10, 0, 6],
});

const collaborator = (record, ctx) => {
  const data = collaboratorData(record, ctx);
  const cell = (pair) =>
    pair
      ? {
          text: [{ text: pair[0], bold: true }, { text: ` ${pair[1]}` }],
          fontSize: 8,
          color: BLUE,
          margin: [3, 4, 3, 4],
        }
      : { text: '' };
  const rows = [];
  for (let i = 0; i < data.length; i += 2) rows.push([cell(data[i]), cell(data[i + 1])]);
  return {
    table: { widths: ['*', '*'], body: rows },
    layout: { defaultBorder: false, fillColor: () => LIGHT_BLUE },
  };
};

const items = (record) => ({
  table: {
    headerRows: 1, // repeated on every page the table runs into
    dontBreakRows: true,
    widths: [32, '*', 70, 86, 62, 46, 102], // 86: a 17-character VIN on one line
    body: [
      ITEM_HEADERS.map((text) => ({
        text,
        bold: true,
        fontSize: 8,
        color: ROW_TEXT,
        margin: [4, 4, 4, 4],
      })),
      ...(record.assets || []).map((asset, index) =>
        itemCells(asset).map((lines, column) => ({
          stack: lines.map((text) => ({ text })),
          fontSize: 7,
          color: ROW_TEXT,
          margin: [4, 4, 4, 4],
          ...(index % 2 ? {} : { fillColor: ROW_GRAY }),
          ...(column === 0 ? { alignment: 'center' } : {}),
        }))
      ),
    ],
  },
  // As the generic voucher: thicker lines around the header, no vertical lines.
  layout: {
    hLineWidth: (i) => (i === 0 || i === 1 ? 2 : 1),
    vLineWidth: () => 0,
    hLineColor: () => ROW_LINE,
  },
});

const declaration = () => ({
  text: DECLARATION,
  fontSize: 8.5,
  alignment: 'justify',
  margin: [0, 2, 0, 4],
});

// The drawn signature takes the space the paper version leaves blank above the
// line. Nodes are built by functions: pdfmake writes its layout into each node,
// and one object used in two cells is drawn only once.
const signatures = (record, ctx) => {
  const { signature } = ctx;
  const small = { fontSize: 8, color: TEXT, alignment: 'center', margin: [4, 3, 4, 3] };
  const line = () => ({ text: BLANK_LINE, fontSize: 8, color: TEXT, alignment: 'center' });
  const blankSpace = () => ({ text: ' ', fontSize: 8, margin: [0, 0, 0, 26] });
  const box = (content) => ({ stack: content, margin: [4, 4, 4, 4] });
  const received = signature
    ? [{ image: signature.image, fit: [160, 34], alignment: 'center' }, line()]
    : [blankSpace(), line()];
  const table = {
    table: {
      widths: [177, 177],
      body: [
        ['ENTREGA', 'RECIBE'].map((text) => ({
          text,
          bold: true,
          fontSize: 8.5,
          color: '#FFFFFF',
          fillColor: BLUE,
          alignment: 'center',
          margin: [0, 3, 0, 3],
        })),
        [box([blankSpace(), line()]), box(received)],
        [
          { ...small, text: deliveredBy(ctx.user) || 'Nombre y puesto' },
          { ...small, text: orNoData(fullName(record)) },
        ],
        [
          { ...small, text: `Fecha: ${BLANK_DATE}` },
          { ...small, text: `Fecha: ${signature ? signedDate(signature) : BLANK_DATE}` },
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0.6,
      vLineWidth: () => 0.6,
      hLineColor: () => '#404040',
      vLineColor: () => '#404040',
    },
  };
  // The section bar goes with its table: never alone at the bottom of a page.
  return {
    unbreakable: true,
    stack: [
      sectionBar('4. FIRMAS DE ENTREGA Y RECEPCION'),
      { columns:[{ width: '*', text: '' }, { width: 'auto', ...table }, { width: '*', text: '' }] },
      ...(signature
        ? [
            {
              text: signatureNote(signature),
              fontSize: 7,
              color: '#6f7a82',
              alignment: 'center',
              margin: [0, 8, 0, 0],
            },
          ]
        : []),
    ],
  };
};

const saafMark = (logoSaaf) => ({
  width: 70,
  stack: [
    {
      text: 'Generado por',
      fontSize: 5,
      color: '#9aa5ad',
      alignment: 'right',
      margin: [0, 0, 0, 3],
    },
    { image: logoSaaf, width: 45, alignment: 'right', opacity: 0.55 },
  ],
});

// The legend centered on the page, the SAAF logo on the right.
const footer = (logoSaaf) => () => ({
  margin: [40, 12, 40, 0],
  columns: [
    { width: 70, text: '' },
    { width: '*', text: FOOTER_TEXT, fontSize: 7, color: '#646464', alignment: 'center' },
    saafMark(logoSaaf),
  ],
});

/**
 * @param {Object} record - the employee and its assets, as a row of the
 *   employees_assets report: {key, name, lastname, position, assets: [{name,
 *   description, brand, model, serie, number, physicalState, locationName,
 *   custodyNotes}]}
 * @param {Object} ctx - {subcompany (name), user (who hands over: the session
 *   user), logoAutosur, logoSaaf, signature?: {image (PNG data URL), signedAt
 *   (DD/MM/YYYY HH:mm), generatedBy}}
 * @returns {Object} pdfmake document definition
 */
export const buildAutosurCustodyVoucher = (record, ctx) => ({
  pageSize: 'LETTER',
  pageMargins: [40, 32, 40, 62], // the Word's 792/648 twips, plus room for the footer
  defaultStyle: { fontSize: 10, color: '#000000', lineHeight: 1.1 },
  info: { title: 'Vale de resguardo de artículos', author: 'SAAF' },
  content: [
    header(ctx),
    sectionBar('1. DATOS DEL COLABORADOR'),
    collaborator(record, ctx),
    sectionBar('2. ARTÍCULOS ENTREGADOS'),
    items(record),
    sectionBar('3. DECLARACIÓN DE RESGUARDO'),
    declaration(),
    signatures(record, ctx),
  ],
  footer: footer(ctx.logoSaaf),
});



// WEBPACK FOOTER //
// src/lib/pdfs/autosur/valeResguardoAutosur.js